import React, { useState } from 'react'
import { Box, Button, Chip, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import moment from 'moment'
import MiddlewareObservationView from './MiddlewareObservationView'
import PostCloseEventsView from './PostCloseEventsView'
import LogErrorBoundary from './LogErrorBoundary'

// Lo que se pinta en el bloque del proveedor. Es tambien lo que decide si ese bloque
// aparece: una transaccion anterior a estos campos no trae ninguno y el bloque se omite
// entero, igual que hacen los demas.
const PROVIDER_KEYS = [
  'serviceName', 'providerName', 'redirectToBackup', 'primaryProvider', 'primaryProviderName',
  'backupProvider', 'backupProviderName', 'providerRequestAtMs', 'providerDurationMs',
  'providerPollCount', 'providerPollsAt', 'providerSku', 'primaryProviderSku'
]

// Llaves que el resumen (TXNSUMMARY) siempre trae y que se pintan en su propia seccion.
// El resto son los campos de negocio que el core adjunta con addSummary().
const SUMMARY_KEYS = [
  'id', 'user', 'method', 'methods', 'startedAt', 'endedAt', 'totalMs',
  'maxSilenceMs', 'maxSilenceAfter', 'slowestStep', 'stepsCount',
  'failedSteps', 'steps', 'events', 'level', 'findSaleByTraceCount',
  ...PROVIDER_KEYS
]



// Cosas que se leen por su nombre y no por su identificador. Se pintan en una sola
// linea: el nombre manda y el identificador queda de respaldo, porque si el core no
// mandara el nombre, perder el dato seria peor que verlo feo.
//
// El proveedor ya sale por su nombre en el bloque de arriba.
const NAMED = [
  { name: 'hosterName', ids: ['hosterId'], label: 'Hoster' },
  { name: 'salepointName', ids: ['salepointId'], label: 'Punto de venta' },
  { name: 'productName', ids: ['productId'], label: 'Producto' },
  // El resumen manda el mismo identificador de bolsa en dos campos distintos.
  { name: 'bagName', ids: ['hosterBagId', 'bagId'], label: 'Bolsa' },
]

const LABELS = {
  operationId: 'Operación',
  typeReg: 'Tipo',
  channel: 'Canal',
  ip: 'IP',
  user: 'Usuario',
  tokenAccess: 'Token',
  providerId: 'Proveedor',
  phoneNumber: 'Número',
  productId: 'Producto',
  amount: 'Monto',
  externalTrace: 'Traza externa',
  hosterBagId: 'Bolsa',
  bagId: 'Bolsa (saldo)',
  hosterId: 'Hoster',
  hosterName: 'Nombre del hoster',
  salepointId: 'Punto de venta',
  salepointName: 'Nombre del punto de venta',
  billingMode: 'Modo de cobro',
  findSaleByTraceCount: 'Consultas FindSaleByTrace',
  serviceName: 'Servicio',
  redirectToBackup: 'Redirigido a backup',
  primaryProvider: 'Proveedor primario',
  responseCode: 'Código',
  responseMessage: 'Respuesta',
  authCode: 'Autorización',
  providerReference: 'Referencia proveedor',
  approved: 'Aprobada',
  ejercidoSaldo: 'Ejerció saldo',
  saldoAnterior: 'Saldo anterior',
  saldoActual: 'Saldo actual',
  discountApplied: 'Descuento',
  errorCode: 'Código error',
  errorMsg: 'Mensaje error',
}

const humanize = (key) => LABELS[key] || key
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^./, (c) => c.toUpperCase())

const duration = (ms) => {
  if (ms === null || ms === undefined || isNaN(ms)) return '-'
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`
}

const value = (val) => {
  if (val === true) return 'Sí'
  if (val === false) return 'No'
  if (val === null || val === undefined || val === '') return '-'
  return String(val)
}

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 160, marginBottom: 1, marginRight: 3 }}>
    <Typography sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, wordBreak: 'break-all' }}>{children}</Typography>
  </Box>
)

/**
 * Muestra el log de una transaccion (corelogs).
 *
 * Si el documento trae el resumen consolidado (`resumen`, publicado por el core en
 * TXNSUMMARY) se pinta la vista de pasos; el texto crudo del campo `message` queda
 * detras del botón "Mostrar detalle". Cuando no hay resumen se muestra el detalle
 * directamente, como antes.
 *
 * La observacion del middleware (`middlewareObservation`) va en su propia seccion y es
 * independiente del resumen: se pinta exista o no, y su ausencia no dice nada del
 * resultado de la transaccion.
 */
const TxnLogContent = ({ data }) => {
  const esObjeto = data && typeof data === 'object' && !Array.isArray(data)
  const resumen = esObjeto ? data.resumen : null
  // La observacion del middleware es opcional y llega aparte del resumen: puede existir
  // sin el, y el resumen puede existir sin ella.
  const observation = esObjeto ? data.middlewareObservation : null
  const detail = typeof data === 'string'
    ? data
    : Array.isArray(data?.message)
      ? data.message.join('\n')
      : (data?.message || '')

  const [showDetail, setShowDetail] = useState(!resumen)

  const steps = Array.isArray(resumen?.steps) ? resumen.steps : []
  const failedSteps = Array.isArray(resumen?.failedSteps) ? resumen.failedSteps : []
  // Hay dos listas de eventos con el mismo contenido casi siempre, y la diferencia
  // importa: `resumen.events` lo arma la propia venta al cerrar, asi que solo contiene lo
  // que esa venta vio, en su contenedor. La lista del documento es el superconjunto: ahi
  // caen tambien las lineas que aporto otra peticion —un FindSaleByTrace desde otro
  // contenedor, o posterior al cierre—. Se prefiere la del documento y se cae a la del
  // resumen, que es lo que llega de un API anterior a este cambio.
  const eventos = Array.isArray(data?.events) && data.events.length > 0
    ? data.events
    : (Array.isArray(resumen?.events) ? resumen.events : [])
  // El resumen solo cuenta las consultas que atendio su propio contenedor con la venta
  // abierta; las demas quedan en la lista de eventos. Ninguna de las dos fuentes es
  // completa por si sola, y el mayor de los dos no puede contar doble: una consulta
  // registrada en las dos suma uno en cada una.
  const consultasEnDocumento = eventos.filter((evento) => evento?.code === 'SALE_QUERY').length
  const consultasTrace = Math.max(consultasEnDocumento, resumen?.findSaleByTraceCount || 0)
  const hayProveedor = PROVIDER_KEYS.some((key) => (
    resumen?.[key] !== undefined && resumen[key] !== null && resumen[key] !== ''
  ))
  const namedKeys = NAMED.reduce((keys, { ids, name }) => [...keys, ...ids, name], [])
  const named = resumen
    ? NAMED
      .map(({ ids, name, label }) => ({
        label,
        content: resumen[name] || ids.map((key) => resumen[key]).find((valor) => valor)
      }))
      .filter(({ content }) => content !== undefined && content !== null && content !== '')
    : []
  const extras = resumen
    ? Object.keys(resumen)
      .filter((key) => !SUMMARY_KEYS.includes(key))
      // Fuera los que ya se pintan por su nombre: si no, cada cosa saldria dos veces.
      .filter((key) => !namedKeys.includes(key))
      .filter((key) => typeof resumen[key] !== 'object' && resumen[key] !== null && resumen[key] !== '')
    : []

  const detailBox = (
    <code style={{ display: 'block', textAlign: 'left', fontSize: 10, whiteSpace: 'pre', overflowX: 'auto' }}>
      {detail || 'Sin detalle'}
    </code>
  )

  // Sin resumen no hay vista de pasos, pero ni la observacion ni los eventos posteriores
  // dependen de el: se pintan igual, encima del texto crudo. Un documento puede traer
  // `events` con `resumen` nulo —quien escribe el log guarda la venta al cerrarla aunque
  // el resumen no haya llegado—, y esconder la cola de la venta por eso seria perderla.
  if (!resumen)
    return (
      <Box sx={{ textAlign: 'left' }}>
        <MiddlewareObservationView observation={observation} />
        <PostCloseEventsView events={eventos} />
        <Box sx={{ marginTop: 1 }}>{detailBox}</Box>
      </Box>
    )

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        <Field label="Métodos">{value(resumen.methods || resumen.method)}</Field>
        <Field label="Usuario">{value(resumen.user)}</Field>
        <Field label="Inicio">{resumen.startedAt ? moment(resumen.startedAt).format('DD/MM/YYYY HH:mm:ss.SSS') : '-'}</Field>
        <Field label="Fin">{resumen.endedAt ? moment(resumen.endedAt).format('DD/MM/YYYY HH:mm:ss.SSS') : '-'}</Field>
        <Field label="Duración total">{duration(resumen.totalMs)}</Field>
        <Field label="Pasos">{value(resumen.stepsCount ?? steps.length)}</Field>
        <Field label="Consultas FindSaleByTrace">{consultasTrace || 'ninguna'}</Field>
        <Field label="Paso más lento">
          {resumen.slowestStep ? `${resumen.slowestStep.label} (${duration(resumen.slowestStep.durationMs)})` : '-'}
        </Field>
        <Field label="Mayor silencio">
          {resumen.maxSilenceMs ? `${duration(resumen.maxSilenceMs)} después de: ${resumen.maxSilenceAfter || '-'}` : '-'}
        </Field>
      </Box>

      <MiddlewareObservationView observation={observation} />

      {hayProveedor && <>
      <Divider sx={{ marginY: 1 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 'bold', marginBottom: 0.5 }}>Proveedor</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        <Field label="Proveedor">{value(resumen.providerName)}</Field>
        <Field label="Conector">{value(resumen.serviceName)}</Field>
        <Field label="Redirigido a backup">
          {resumen.redirectToBackup
            ? `Sí → ${resumen.backupProviderName || resumen.backupProvider || resumen.providerName || '-'}`
            : 'No'}
        </Field>
        {resumen.redirectToBackup && (
          <Field label="Proveedor primario">
            {value(resumen.primaryProviderName || resumen.primaryProvider)}
          </Field>
        )}
        <Field label="Envío al proveedor">
          {resumen.providerRequestAtMs === null || resumen.providerRequestAtMs === undefined
            ? '-'
            : `a los ${duration(resumen.providerRequestAtMs)} de iniciada`}
        </Field>
        <Field label="Producto solicitado">{value(resumen.providerSku)}</Field>
        {resumen.redirectToBackup && (
          <Field label="Producto solicitado al primario">{value(resumen.primaryProviderSku)}</Field>
        )}
        <Field label="Respuesta del proveedor">{duration(resumen.providerDurationMs)}</Field>
        <Field label="Consultas de estatus">
          {resumen.providerPollCount
            ? `${resumen.providerPollCount} (${resumen.providerPollsAt || 'sin detalle'})`
            : 'ninguna'}
        </Field>
      </Box>
      </>}

      {(named.length > 0 || extras.length > 0) && <>
        <Divider sx={{ marginY: 1 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {named.map(({ label, content }) => (
            <Field key={label} label={label}>{value(content)}</Field>
          ))}
          {extras.map((key) => (
            <Field key={key} label={humanize(key)}>{value(resumen[key])}</Field>
          ))}
        </Box>
      </>}

      {failedSteps.length > 0 && <>
        <Divider sx={{ marginY: 1 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: 'error.main', marginBottom: 0.5 }}>
          Pasos con error
        </Typography>
        {failedSteps.map((step, index) => (
          <Typography key={index} sx={{ fontSize: 12, color: 'error.main' }}>
            {step.label} ({duration(step.durationMs)}): {step.error || '-'}
          </Typography>
        ))}
      </>}

      {steps.length > 0 && <>
        <Divider sx={{ marginY: 1 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 'bold', marginBottom: 0.5 }}>Pasos</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 11, padding: '4px 8px', width: 30 }}>#</TableCell>
              <TableCell sx={{ fontSize: 11, padding: '4px 8px' }}>Paso</TableCell>
              <TableCell sx={{ fontSize: 11, padding: '4px 8px', width: 90 }}>Duración</TableCell>
              <TableCell sx={{ fontSize: 11, padding: '4px 8px', width: 80 }}>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {steps.map((step, index) => (
              <TableRow key={index}>
                <TableCell sx={{ fontSize: 11, padding: '4px 8px' }}>{index + 1}</TableCell>
                <TableCell sx={{ fontSize: 11, padding: '4px 8px' }}>
                  {step.label}
                  {step.error && <Typography sx={{ fontSize: 10, color: 'error.main' }}>{step.error}</Typography>}
                </TableCell>
                <TableCell sx={{ fontSize: 11, padding: '4px 8px' }}>{duration(step.durationMs)}</TableCell>
                <TableCell sx={{ fontSize: 11, padding: '4px 8px' }}>
                  <Chip
                    size="small"
                    label={step.status === 'error' ? 'Error' : step.status === 'running' ? 'En curso' : 'OK'}
                    color={step.status === 'error' ? 'error' : step.status === 'running' ? 'warning' : 'success'}
                    sx={{ fontSize: 10, height: 18 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>}

      <PostCloseEventsView events={eventos} />

      <Divider sx={{ marginY: 1 }} />
      <Button
        size="small"
        variant="outlined"
        onClick={() => setShowDetail(!showDetail)}
      >
        {showDetail ? 'Ocultar detalle' : 'Mostrar detalle'}
      </Button>
      {showDetail && <Box sx={{ marginTop: 1 }}>{detailBox}</Box>}
    </Box>
  )
}

// Un dato malformado en el log no puede tirar la pantalla que lo muestra: el fallo se
// queda en este bloque (ver LogErrorBoundary).
const TxnLogView = ({ data }) => (
  <LogErrorBoundary resetKey={data}>
    <TxnLogContent data={data} />
  </LogErrorBoundary>
)

export default TxnLogView
